import React, { createContext, useState, type HTMLAttributes, useRef } from 'react'
import styled from 'styled-components'
import Overlay from '../../components/Overlay/Overlay'
import { Handler } from './types'

interface ModalsContext {
  onPresent: (node: React.ReactNode, key?: string) => void
  onDismiss: Handler
  setCloseOnOverlayClick: React.Dispatch<React.SetStateAction<boolean>>
  setOnOverlayDismiss: React.Dispatch<React.SetStateAction<() => void>>
  setDismissCallback: React.Dispatch<React.SetStateAction<() => void>>
}

const ModalWrapper = styled.div<HTMLAttributes<HTMLDivElement>>`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  position: fixed;
  top: 0;
  right: 0;
  bottom: 0;
  left: 0;
  z-index: ${({ theme }) => theme.zIndices.modalOverlay};
  /*
   * Stops a wheel gesture that reaches the end of the modal's own scroll from
   * chaining onward and scrolling the page behind it.
   */
  overscroll-behavior: contain;
`

export const Context = createContext<ModalsContext>({
  onPresent: () => null as unknown,
  onDismiss: () => null as unknown,
  setCloseOnOverlayClick: () => true,
  setOnOverlayDismiss: () => null,
  setDismissCallback: () => null
})

const ModalProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [isOpen, setIsOpen] = useState(false)
  const [modalNode, setModalNode] = useState<React.ReactNode>()
  const [closeOnOverlayClick, setCloseOnOverlayClick] = useState(true)
  const [onOverlayDismiss, setOnOverlayDismiss] = useState(() => () => null as unknown)
  const [dismissCallback, setDismissCallback] = useState(() => () => null as unknown)

  /** The page's own overflow, captured while a modal takes over scrolling. */
  const previousBodyOverflow = useRef('')

  const handlePresent = (node: React.ReactNode) => {
    setModalNode(node)
    setIsOpen(true)
    // Remember what the page had, so dismissing restores it instead of
    // imposing 'auto' on a document that may never have used it.
    previousBodyOverflow.current = document.body.style.overflow
    document.body.style.overflow = 'hidden'
  }

  const handleDismiss = () => {
    setModalNode(undefined)
    setIsOpen(false)
    dismissCallback()
    document.body.style.overflow = previousBodyOverflow.current
  }

  const handleOverlayDismiss = () => {
    if (closeOnOverlayClick) {
      handleDismiss()
      onOverlayDismiss()
    }
  }

  return (
    <Context.Provider
      value={{
        onPresent: handlePresent,
        onDismiss: handleDismiss,
        setCloseOnOverlayClick,
        setOnOverlayDismiss,
        setDismissCallback
      }}
    >
      {isOpen && (
        <ModalWrapper>
          <Overlay show onClick={handleOverlayDismiss} />
          {React.isValidElement(modalNode) &&
            React.cloneElement(modalNode, {
              // @ts-ignore
              onDismiss: handleDismiss
            })}
        </ModalWrapper>
      )}
      {children}
    </Context.Provider>
  )
}

export default ModalProvider
